/**
 * 회사 용어집 — 관리 콘솔 (2026-10-23 · 마이그 240 · 시안 G 2판).
 *
 * 서버 `apps/chat-server/src/modules/voice/voice-glossary.routes.ts` 의 칸 그대로다 — 이름을 바꾸지 않는다.
 *
 * ## 「아직 없음」은 둘이다 — 고장이 아니다
 *
 *   · 404 — 그 판의 chat-server 가 아직 안 올라왔다(배포가 chat-server 먼저 · 콘솔 나중이라 창이 생긴다)
 *   · `available: false` — 서버는 섰는데 마이그 240 이 아직 안 걸렸다
 * ⇒ 둘 다 빨간 오류로 그리지 않는다(음성 저장 화면과 같은 까닭). 500 · 502 는 «진짜 탈»이라 오류로 둔다.
 *
 * ## ⛔ 붙일 자리(`place`)에 기본값이 없다 — 사용자 답 「넣을 때 고르기」
 * 콘솔이 빠뜨리면 서버가 400 PLACE_REQUIRED 로 거절한다.
 */
import { AxiosError } from 'axios';
import { apiClient } from './client';

export type GlossaryState = 'in' | 'out' | 'off';
export type GlossaryPlace = 'top' | 'bottom';
export type GlossaryMove = 'up' | 'down' | 'top' | 'bottom';

export interface GlossaryTerm {
  id: string;
  term: string;
  memo: string | null;
  enabled: boolean;
  position: number;
  /** in = 힌트에 들어감 · out = 한도 밖(켜 두어도) · off = 꺼짐 — 받아 적기와 같은 함수로 서버가 정한다. */
  state: GlossaryState;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface GlossaryView {
  available: boolean;
  budget_chars: number;
  used_chars: number;
  in_hint: number;
  enabled: number;
  total: number;
  max_terms: number;
  term_max: number;
  memo_max: number;
  terms: GlossaryTerm[];
}

export interface GlossaryReject {
  line: number;
  chars: number;
  field: 'term' | 'memo';
}

/** 넣기 계획 — 미리 보기와 실제 넣기가 서버의 «같은» 함수다. */
export interface GlossaryPlan {
  place: GlossaryPlace;
  added: number;
  added_terms: string[];
  /** 새 줄이 들어갈 차례(1부터). */
  new_positions: number[];
  new_in_hint: string[];
  /** 넣기 전엔 힌트에 들었는데 넣은 뒤 밀려나는 기존 용어. */
  pushed_out: string[];
  skipped_existing: string[];
  too_long: GlossaryReject[];
  /** 넣은 뒤 남는 자리 — 음수면 상한(500)을 넘는다. */
  room: number;
  over_limit: boolean;
}

export interface GlossaryInstitution {
  institution_id: string;
  code: string | null;
  name: string;
  term_count: number;
}

export type GlossaryInstitutionsResult =
  | { ready: true; available: boolean; institutions: GlossaryInstitution[] }
  /** 그 판이 아직 안 올라온 서버다(404) — 고장이 아니다. */
  | { ready: false };

export interface GlossaryAddResult extends Partial<GlossaryView> {
  dry_run: boolean;
  plan: GlossaryPlan;
}

export interface GlossaryDeleted {
  term: string;
  memo: string | null;
  enabled: boolean;
  position: number;
}

const BASE = '/admin/voice/glossary';

function isNotServedYet(e: unknown): boolean {
  // ⚠ 404 만 본다 — 500·502 는 «진짜 탈»이라 오류로 보여야 한다.
  return (e as AxiosError | undefined)?.response?.status === 404;
}

/** 기관마다 용어 수 — 회사마다 따로 부르지 않는다(사람당 분당 버킷). */
export async function listGlossaryInstitutions(): Promise<GlossaryInstitutionsResult> {
  try {
    const { data } = await apiClient.get<{ available: boolean; institutions: GlossaryInstitution[] }>(BASE);
    return { ready: true, available: data.available, institutions: data.institutions };
  } catch (e) {
    if (isNotServedYet(e)) return { ready: false };
    throw e;
  }
}

export async function getGlossary(inst: string): Promise<GlossaryView> {
  const { data } = await apiClient.get<GlossaryView>(`${BASE}/${inst}`);
  return data;
}

export async function addGlossaryTerm(
  inst: string,
  body: { term: string; memo: string | null; enabled: boolean; place: GlossaryPlace; dry_run: boolean },
): Promise<GlossaryAddResult> {
  const { data } = await apiClient.post<GlossaryAddResult>(`${BASE}/${inst}`, body);
  return data;
}

export async function addGlossaryBulk(
  inst: string,
  body: { text: string; place: GlossaryPlace; dry_run: boolean },
): Promise<GlossaryAddResult> {
  const { data } = await apiClient.post<GlossaryAddResult>(`${BASE}/${inst}/bulk`, body);
  return data;
}

export async function patchGlossaryTerm(
  inst: string,
  id: string,
  body: { term?: string; memo?: string | null; enabled?: boolean },
): Promise<GlossaryView> {
  const { data } = await apiClient.patch<GlossaryView>(`${BASE}/${inst}/${id}`, body);
  return data;
}

export async function moveGlossaryTerm(inst: string, id: string, to: GlossaryMove): Promise<GlossaryView> {
  const { data } = await apiClient.post<GlossaryView>(`${BASE}/${inst}/${id}/move`, { to });
  return data;
}

export async function deleteGlossaryTerm(inst: string, id: string): Promise<GlossaryView & { deleted: GlossaryDeleted }> {
  const { data } = await apiClient.delete<GlossaryView & { deleted: GlossaryDeleted }>(`${BASE}/${inst}/${id}`);
  return data;
}

export async function restoreGlossaryTerm(inst: string, deleted: GlossaryDeleted): Promise<GlossaryView> {
  const { data } = await apiClient.post<GlossaryView>(`${BASE}/${inst}/restore`, deleted);
  return data;
}
